import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectService, Project } from '../services/projectService';
import { Button } from '../components/Button';
import { toast } from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';

export const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await projectService.getProjectById(id!);
        setProject(data);
      } catch (error) {
        console.error('Error fetching project:', error);
        setError('Failed to load project details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProject();
    }
  }, [id]);

  const formatRevenue = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const handleContactSeller = () => {
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
      return;
    }
    // TODO: Implement messaging system
    toast.success('Messaging system coming soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="h-96 bg-gray-200 rounded-lg mb-8"></div>
                <div className="space-y-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="h-32 bg-gray-200 rounded"></div>
                <div className="h-32 bg-gray-200 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error || 'Project not found'}
          </div>
          <Button
            onClick={() => navigate('/browse')}
            className="mt-4"
          >
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{project.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span>Category: {project.category || 'Uncategorized'}</span>
              <span>•</span>
              <span>Status: {project.status}</span>
              <span>•</span>
              <span>Listed: {new Date(project.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
          <Button
            onClick={handleContactSeller}
            className="bg-primary-600 text-white hover:bg-primary-700"
          >
            Contact Seller
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Project Image */}
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <img
                src={project.imageUrl || '/placeholder.png'}
                alt={project.title}
                className="w-full h-96 object-cover"
              />
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Description</h2>
              <div className="prose max-w-none">
                <p className="text-gray-600 whitespace-pre-wrap">{project.description}</p>
              </div>
            </div>

            {/* Tags */}
            {project.tags && project.tags.length > 0 && (
              <div className="bg-white rounded-xl shadow-soft p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Tags</h2>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Card */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Price</h2>
              <div className="text-3xl font-bold text-primary-600 mb-4">
                {project.price ? formatRevenue(project.price) : 'Contact for Price'}
              </div>
              <Button
                onClick={handleContactSeller}
                className="w-full bg-primary-600 text-white hover:bg-primary-700"
              >
                Contact Seller
              </Button>
            </div>

            {/* Project Details */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Project Details</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Project Type</h3>
                  <p className="text-gray-900">{project.projectType}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Revenue Status</h3>
                  <p className="text-gray-900">{project.revenueStatus}</p>
                </div>
                {project.revenueStatus === 'revenue' && (
                  <>
                    {project.monthlyRevenue && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Monthly Revenue</h3>
                        <p className="text-gray-900">{formatRevenue(project.monthlyRevenue)}</p>
                      </div>
                    )}
                    {project.annualRevenue && (
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Annual Revenue</h3>
                        <p className="text-gray-900">{formatRevenue(project.annualRevenue)}</p>
                      </div>
                    )}
                  </>
                )}
                {project.userCount && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Number of Users</h3>
                    <p className="text-gray-900">{project.userCount.toLocaleString()}</p>
                  </div>
                )}
                {project.ytdGrowth && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">YTD Growth</h3>
                    <p className={`text-gray-900 ${project.ytdGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {project.ytdGrowth >= 0 ? '+' : ''}{project.ytdGrowth}%
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Licensing */}
            <div className="bg-white rounded-xl shadow-soft p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Licensing</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <svg
                    className={`w-5 h-5 ${project.licensing.includeSourceCode ? 'text-green-500' : 'text-red-500'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={project.licensing.includeSourceCode ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"}
                    />
                  </svg>
                  <span className="text-gray-900">Source Code Included</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg
                    className={`w-5 h-5 ${project.licensing.transferOwnership ? 'text-green-500' : 'text-red-500'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={project.licensing.transferOwnership ? "M5 13l4 4L19 7" : "M6 18L18 6M6 6l12 12"}
                    />
                  </svg>
                  <span className="text-gray-900">Full Ownership Transfer</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 